import { upperFirst } from 'lodash-es'
import { useState } from 'react'

import { FvButton, Icon, ManualTrackForm } from '@fv/client-components'
import { getCurrentSequence } from '@fv/client-core'
import type { FullShipment } from '@fv/client-types'
import type { EquipmentType } from '@fv/models/equipment'

import { useUpdateTrackStatus } from '@/features/track/useUpdateTrackStatus'
import { useTypeNames } from '@/hooks/settings'

import { DividedContent } from '../DividedContent'
import { Pinline } from '../Pinline'
import { DetailPanelLayout } from './DetailPanelLayout'
import { UpdateTrackingSequenceForm } from './UpdateTrackingSequenceForm'

type UpdateTrackingPanelProps = {
  closePanel: () => void
  load: FullShipment
}

const UpdateTrackingPanel = ({
  closePanel,
  load,
}: UpdateTrackingPanelProps) => {
  const { loadId, locations, tracking } = load
  const { equipmentName } = useTypeNames()
  const [nextSequence, setNextSequence] = useState('')
  const currentSequence = getCurrentSequence(tracking)
  const updateTrackStatus = useUpdateTrackStatus(loadId)
  const trackSequence = nextSequence || String(currentSequence)
  const truckSummary = composeSummary(load, equipmentName)

  return (
    <DetailPanelLayout
      header={
        <DividedContent>
          <FvButton onClick={closePanel}>
            <Icon icon="arrow-to-left" />
            <span>Back</span>
          </FvButton>
        </DividedContent>
      }
    >
      <p className="mb-0 flex flex-nowrap items-start">
        <Icon className="text-fv-gray-dark" icon="truck" transform="down-2" />
        <span>{truckSummary}</span>
      </p>

      {!nextSequence ? (
        <>
          <Pinline className="mt-2 mb-8" />
          <UpdateTrackingSequenceForm
            currentSequence={Number(trackSequence)}
            locations={locations}
            setSequence={setNextSequence}
          />
        </>
      ) : (
        <>
          <Pinline className="my-2" />
          <ManualTrackForm
            closeForm={closePanel}
            initialSequence={Number(nextSequence)}
            loading={updateTrackStatus.isPending}
            locations={locations}
            onSave={updateTrackStatus.mutate}
          />
        </>
      )}
    </DetailPanelLayout>
  )
}

function composeRefNums({ refNums }: FullShipment) {
  let refNumsDisplay = ''

  if (refNums?.length > 0) {
    if (refNumsDisplay) refNumsDisplay += ' / '
    refNumsDisplay += `Ref# ${refNums.map(n => n.value).join(' / ')}`
  }

  return refNumsDisplay
}

function composeSummary(
  load: FullShipment,
  getEquipmentName: (key: EquipmentType) => string,
) {
  const { equipment, selectedQuote, workflow } = load
  const hasName = workflow === 'truckload'
  const name = hasName
    ? getEquipmentName(selectedQuote?.equipmentType ?? equipment.type)
    : ''

  const equipmentDescription = equipment.description
    ? upperFirst(equipment.description)
    : ''

  const refNumsDisplay = composeRefNums(load)
  const noDescriptionDescription =
    hasName || equipmentDescription || refNumsDisplay
      ? ''
      : 'No description given'

  return [refNumsDisplay, name, equipmentDescription, noDescriptionDescription]
    .filter(v => !!v)
    .join(' / ')
}

export default UpdateTrackingPanel
